import { z } from "zod";
import {
  PasswordResetConfirmIn,
  PasswordResetInfoOut,
  PasswordResetRequestIn,
  PasswordResetSuccessOut,
} from "@/lib/api/schemas/passwordReset";
import { Auth } from "../auth";
import type { ActionReturnType } from "../types";
import { request, validateInput } from "../request";

class PasswordResetEndpoint {
  public static readonly basePath = "password-reset";
  public static readonly schemas = {
    requestReset: {
      body: PasswordResetRequestIn,
      response: PasswordResetSuccessOut,
    },
    getReset: {
      response: PasswordResetInfoOut,
    },
    confirmReset: {
      body: PasswordResetConfirmIn,
      response: PasswordResetSuccessOut,
    },
  };

  constructor(private api: Auth) {}

  public async requestReset(
    body: z.input<typeof PasswordResetEndpoint.schemas.requestReset.body>,
  ): ActionReturnType<z.infer<typeof PasswordResetEndpoint.schemas.requestReset.response>> {
    const bodyValidated = validateInput(PasswordResetEndpoint.schemas.requestReset.body, body);
    if (!bodyValidated.ok) return bodyValidated;
    return request({
      log: ["PasswordReset", "requestReset"],
      method: "post",
      path: [PasswordResetEndpoint.basePath],
      json: bodyValidated.data,
      response: PasswordResetEndpoint.schemas.requestReset.response,
      skipAuth: true,
      localErrorHandling: true,
    });
  }

  public getReset(token: string): ActionReturnType<z.infer<typeof PasswordResetEndpoint.schemas.getReset.response>> {
    return request({
      log: ["PasswordReset", "getReset"],
      method: "get",
      path: [PasswordResetEndpoint.basePath, token],
      response: PasswordResetEndpoint.schemas.getReset.response,
      skipAuth: true,
      localErrorHandling: true,
    });
  }

  public async confirmReset(
    body: z.input<typeof PasswordResetEndpoint.schemas.confirmReset.body>,
  ): ActionReturnType<z.infer<typeof PasswordResetEndpoint.schemas.confirmReset.response>> {
    const bodyValidated = validateInput(PasswordResetEndpoint.schemas.confirmReset.body, body);
    if (!bodyValidated.ok) return bodyValidated;
    return request({
      log: ["PasswordReset", "confirmReset"],
      method: "post",
      path: [PasswordResetEndpoint.basePath, "confirm"],
      json: bodyValidated.data,
      response: PasswordResetEndpoint.schemas.confirmReset.response,
      skipAuth: true,
      localErrorHandling: true,
    });
  }
}

export { PasswordResetEndpoint };
